import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import AuthLayout from './AuthLayout';
import { TextField, PasswordField, SubmitButton, EMAIL_RE } from './FormFields';
import { useAuth } from '../../auth/AuthContext';
import { safeRedirect, ROLE_LABELS } from '../../config/roles';
import appConfig from '../../config/appConfig';
import { Mail, Lock, Check } from '../Icons';

const REASONS = {
  idle: `You were signed out after ${appConfig.auth.idleTimeoutMinutes} minutes of inactivity.`,
  expired: 'Your session expired. Please sign in again.',
  'other-tab': 'You signed out in another tab.',
  logout: "You've been signed out.",
};

// Friction after repeated failures. The server must enforce real rate limiting.
const MAX_ATTEMPTS = 5;
const LOCK_SECONDS = 30;

// Demo accounts exist only when mock auth is on (never in production builds).
const demoAccounts = () =>
  process.env.NODE_ENV !== 'production' && appConfig.auth.useMockAuth ? require('../../auth/mockAuthService') : null;

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const { login, reason } = useAuth();
  const notice = location.state?.notice || REASONS[reason];

  const [form, setForm] = useState({ email: location.state?.email || '', password: '', remember: true });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [failures, setFailures] = useState(0);
  const [lockedFor, setLockedFor] = useState(0);

  useEffect(() => {
    if (!lockedFor) return undefined;
    const t = setTimeout(() => setLockedFor((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [lockedFor]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const validate = (f) => {
    const er = {};
    if (!f.email.trim()) er.email = 'Enter your email address.';
    else if (!EMAIL_RE.test(f.email.trim())) er.email = 'Enter a valid email address.';
    if (!f.password) er.password = 'Enter your password.';
    return er;
  };

  const signIn = async (credentials) => {
    if (lockedFor) return;
    setFormError('');
    const er = validate(credentials);
    setErrors(er);
    if (Object.keys(er).length) return;

    setLoading(true);
    try {
      const user = await login(credentials);
      navigate(safeRedirect(params.get('next'), user.role), { replace: true });
    } catch (err) {
      const n = failures + 1;
      setFailures(n);
      if (n >= MAX_ATTEMPTS) {
        setFailures(0);
        setLockedFor(LOCK_SECONDS);
      }
      // Same message for unknown email and wrong password, so accounts can't be discovered.
      setFormError(err.status === 401 || err.status === 400 ? 'Incorrect email or password. Please try again.' : err.message);
      setForm((f) => ({ ...f, password: '' }));
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    signIn(form);
  };

  const demo = demoAccounts();

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Log in to manage your deliveries and journeys."
      footer={<>New to PinParcels? <Link to="/register">Create an account</Link></>}
    >
      {notice && (
        <div className="alert alert--success" role="status">
          <Check width={18} height={18} /> {notice}
        </div>
      )}
      {params.get('next') && !notice && <div className="alert alert--info" role="status">Please log in to continue.</div>}
      {formError && <div className="alert alert--error" role="alert">{formError}</div>}
      {lockedFor > 0 && <div className="alert alert--error" role="alert">Too many attempts. Try again in {lockedFor}s.</div>}

      <form className="auth__form" onSubmit={onSubmit} noValidate>
        <TextField
          id="email"
          label="Email address"
          type="email"
          icon={Mail}
          placeholder="you@example.com"
          autoComplete="username"
          value={form.email}
          onChange={onChange}
          error={errors.email}
          autoFocus
        />
        <PasswordField
          id="password"
          label="Password"
          icon={Lock}
          placeholder="Enter your password"
          autoComplete="current-password"
          value={form.password}
          onChange={onChange}
          error={errors.password}
          right={<Link to="/forgot-password" className="fld__link">Forgot password?</Link>}
        />

        <label className="checkbox">
          <input type="checkbox" name="remember" checked={form.remember} onChange={onChange} />
          <span className="checkbox__box"><Check width={12} height={12} /></span>
          Keep me logged in on this device
        </label>

        <SubmitButton loading={loading}>{loading ? 'Logging in…' : lockedFor ? `Try again in ${lockedFor}s` : 'Log in'}</SubmitButton>
      </form>

      {process.env.NODE_ENV !== 'production' && demo && (
        <div className="auth__demo">
          <span>Demo accounts (development only · password {demo.DEMO_PASSWORD})</span>
          <div>
            {demo.DEMO_ACCOUNTS.map((a) => (
              <button type="button" key={a.id} disabled={loading} onClick={() => signIn({ email: a.email, password: demo.DEMO_PASSWORD, remember: false })}>
                {ROLE_LABELS[a.role]}
              </button>
            ))}
          </div>
        </div>
      )}
    </AuthLayout>
  );
}
